import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import WizardProgressIndicator from "components/ui/WizardProgressIndicator";
import Icon from "components/AppIcon";
import { useConfig } from "../../context/ConfigContext";

const ExportSummary = () => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const [isExporting, setIsExporting] = useState(false);
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const [exportFormat, setExportFormat] = useState("pdf");
  const [showPreview, setShowPreview] = useState(false);

  // Calculate all results based on input data (same logic as results dashboard)
  const calculateResults = (inputData) => {
    if (!config) return null; // Wait for config to load

    // Standard Process calculations
    const standardFeePerUnit = config.standard_processing_fee;
    const standardTransportation = -config.standard_transportation;
    const standardProcessingFee = -(
      inputData.materialWeight * standardFeePerUnit
    );
    const standardProjectTotal = standardProcessingFee + standardTransportation;

    // Donation Model calculations
    const looptFeePerPound = config.donation_processing_fee;
    const looptTransportation = -config.donation_transportation;
    const looptProcessingFee = -(
      inputData.looptMaterialWeight * looptFeePerPound
    );
    const looptCogsGross = inputData.looptUnits * inputData.looptCogsPerUnit;
    const looptFmv = looptCogsGross;
    const looptCorporateTaxRateDecimal = inputData.looptCorporateTaxRate / 100;
    const looptEbitaPositiveImpact = looptFmv * looptCorporateTaxRateDecimal;
    const looptNetEbitaContribution =
      looptProcessingFee + looptTransportation + looptEbitaPositiveImpact;

    // Revenue calculations - using material weight for rag trader, units for value channel
    const ragTraderRevenue = inputData.materialWeight * config.rag_trader_rate;
    const valueChannelRevenue = inputData.units * config.value_channel_rate;

    return {
      inputs: inputData,
      standardProcess: {
        material: inputData.material,
        units: inputData.units,
        materialWeight: inputData.materialWeight,
        feePerUnit: standardFeePerUnit,
        processingFee: standardProcessingFee,
        transportationEstimate: standardTransportation,
        projectTotal: standardProjectTotal,
      },
      looptFoundation: {
        material: inputData.material,
        units: inputData.looptUnits,
        materialWeight: inputData.looptMaterialWeight,
        cogsPerUnit: inputData.looptCogsPerUnit,
        feePerPound: looptFeePerPound,
        transportationEstimate: looptTransportation,
        corporateTaxRate: looptCorporateTaxRateDecimal,
        processingFee: looptProcessingFee,
        cogsGross: looptCogsGross,
        fmv: looptFmv,
        ebitaPositiveImpact: looptEbitaPositiveImpact,
        netEbitaContribution: looptNetEbitaContribution,
      },
      revenue: {
        ragTraderWeight: inputData.materialWeight,
        ragTraderRevenue: ragTraderRevenue,
        valueChannelWeight: inputData.materialWeight,
        valueChannelRevenue: valueChannelRevenue,
      },
    };
  };

  // Get calculation results from localStorage
  const getCalculationResults = () => {
    const savedData = localStorage.getItem("materialInputData");
    if (!savedData) return null;

    try {
      const parsedData = JSON.parse(savedData);
      const inputData = {
        material: parsedData.materialName || "Unknown Material",
        units: parseFloat(parsedData.units) || 0,
        materialWeight: parseFloat(parsedData.materialWeight) || 0,
        cogsPerUnit: parseFloat(parsedData.looptCogsPerUnit) || 0,
        // Donation Model specific inputs
        looptUnits:
          parseFloat(parsedData.looptUnits) ||
          parseFloat(parsedData.units) ||
          0,
        looptMaterialWeight:
          parseFloat(parsedData.looptMaterialWeight) ||
          parseFloat(parsedData.materialWeight) ||
          0,
        looptCogsPerUnit: parseFloat(parsedData.looptCogsPerUnit) || 0,
        looptCorporateTaxRate:
          parseFloat(parsedData.looptCorporateTaxRate) || 20,
        // Revenue inputs
        ragTraderWeight:
          parseFloat(parsedData.ragTraderWeight) ||
          parseFloat(parsedData.materialWeight) ||
          0,
        valueChannelWeight:
          parseFloat(parsedData.valueChannelWeight) ||
          parseFloat(parsedData.materialWeight) ||
          0,
      };

      return calculateResults(inputData);
    } catch (error) {
      console.error("Error parsing saved data:", error);
      return null;
    }
  };

  const calculationResults = getCalculationResults();

  // Formatting functions
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatCost = (value) => {
    return formatCurrency(Math.abs(value));
  };

  // Generate PDF Report
  const generatePDFReport = (data) => {
    const reportDate = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Textile Cost Calculation Report</title>
    <style>
        @page {
            size: A4;
            margin: 0.75in;
        }

        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11px;
            line-height: 1.4;
            color: #1f2937;
            background: white;
        }

        .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #3b82f6;
        }

        .header h1 {
            font-size: 24px;
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 8px;
        }

        .header .subtitle {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 4px;
        }

        .header .date {
            font-size: 12px;
            color: #9ca3af;
        }

        .section {
            margin-bottom: 25px;
        }

        .section-title {
            font-size: 16px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 12px;
            padding: 8px 12px;
            background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
            border-left: 4px solid #3b82f6;
        }

        .input-summary {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 25px;
        }

        .input-group {
            background: #f9fafb;
            padding: 15px;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
        }

        .input-group h4 {
            font-size: 13px;
            font-weight: bold;
            color: #374151;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .input-item {
            display: flex;
            justify-content: space-between;
            margin-bottom: 6px;
            font-size: 11px;
        }

        .input-item .label {
            color: #6b7280;
        }

        .input-item .value {
            font-weight: 600;
            color: #1f2937;
        }

        .results-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .results-table th {
            background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-size: 12px;
            font-weight: 600;
            border: 1px solid #2563eb;
        }

        .results-table td {
            padding: 10px 8px;
            border: 1px solid #e5e7eb;
            font-size: 11px;
        }

        .results-table tbody tr:nth-child(even) {
            background: #f9fafb;
        }

        .results-table tbody tr:hover {
            background: #f3f4f6;
        }

        .metric-label {
            font-weight: 500;
            color: #374151;
        }

        .currency {
            font-weight: 600;
            text-align: right;
        }

        .positive {
            color: #059669;
        }

        .negative {
            color: #dc2626;
        }

        .neutral {
            color: #6b7280;
        }

        .summary-cards {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 15px;
            margin-top: 20px;
        }

        .summary-card {
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            padding: 15px;
            border-radius: 8px;
            border: 1px solid #00B693;
            text-align: center;
        }

        .summary-card h4 {
            font-size: 12px;
            color: #00B693;
            margin-bottom: 8px;
            font-weight: 600;
        }

        .summary-card .value {
            font-size: 16px;
            font-weight: bold;
            color: #231F20;
        }

        .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            font-size: 10px;
            color: #9ca3af;
        }

        @media print {
            body { print-color-adjust: exact; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Textile Cost Calculation Report</h1>
        <div class="subtitle">Comprehensive Analysis of Processing Costs and Revenue Opportunities</div>
        <div class="date">Generated on ${reportDate}</div>
    </div>

    <div class="section">
        <div class="section-title">Input Summary</div>
        <div class="input-summary">
            <div class="input-group">
                <h4>Material Information</h4>
                <div class="input-item">
                    <span class="label">Material Type:</span>
                    <span class="value">${data.inputs.material}</span>
                </div>
                <div class="input-item">
                    <span class="label">Standard Units:</span>
                    <span class="value">${formatNumber(
                      data.inputs.units
                    )}</span>
                </div>
                <div class="input-item">
                    <span class="label">Standard Weight:</span>
                    <span class="value">${formatNumber(
                      data.inputs.materialWeight
                    )} LBS</span>
                </div>
            </div>

            <div class="input-group">
                <h4>Donation Model Inputs</h4>
                <div class="input-item">
                    <span class="label">Donation Units:</span>
                    <span class="value">${formatNumber(
                      data.inputs.looptUnits
                    )}</span>
                </div>
                <div class="input-item">
                    <span class="label">Donation Weight:</span>
                    <span class="value">${formatNumber(
                      data.inputs.looptMaterialWeight
                    )} LBS</span>
                </div>
                <div class="input-item">
                    <span class="label">COGS per Unit:</span>
                    <span class="value">${formatCurrency(
                      data.inputs.looptCogsPerUnit
                    )}</span>
                </div>
                <div class="input-item">
                    <span class="label">Corporate Tax Rate:</span>
                    <span class="value">${formatNumber(
                      data.inputs.looptCorporateTaxRate
                    )}%</span>
                </div>
            </div>
        </div>
    </div>

    <div class="section">
        <div class="section-title">Processing Cost Analysis</div>
        <table class="results-table">
            <thead>
                <tr>
                    <th>Processing Method</th>
                    <th>Processing Fee</th>
                    <th>Transportation</th>
                    <th>Total Cost</th>
                    <th>Additional Benefits</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class="metric-label">Standard Process</td>
                    <td class="currency negative">${formatCost(
                      data.standardProcess.processingFee
                    )}</td>
                    <td class="currency negative">${formatCost(
                      data.standardProcess.transportationEstimate
                    )}</td>
                    <td class="currency negative">${formatCost(
                      data.standardProcess.projectTotal
                    )}</td>
                    <td class="neutral">None</td>
                </tr>
                <tr>
                    <td class="metric-label">Donation Model</td>
                    <td class="currency negative">${formatCost(
                      data.looptFoundation.processingFee
                    )}</td>
                    <td class="currency negative">${formatCost(
                      data.looptFoundation.transportationEstimate
                    )}</td>
                    <td class="currency negative">${formatCost(
                      data.looptFoundation.processingFee +
                        data.looptFoundation.transportationEstimate
                    )}</td>
                    <td class="currency positive">${formatCurrency(
                      data.looptFoundation.ebitaPositiveImpact
                    )} Tax Benefit</td>
                </tr>
            </tbody>
        </table>
    </div>

    <div class="section">
        <div class="section-title">Donation Model Detailed Calculations</div>
        <table class="results-table">
            <thead>
                <tr>
                    <th>Calculation Step</th>
                    <th>Formula</th>
                    <th>Result</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class="metric-label">COGS Gross</td>
                    <td>Units × COGS per Unit</td>
                    <td class="currency">${formatCurrency(
                      data.looptFoundation.cogsGross
                    )}</td>
                </tr>
                <tr>
                    <td class="metric-label">Fair Market Value (FMV)</td>
                    <td>Same as COGS Gross</td>
                    <td class="currency">${formatCurrency(
                      data.looptFoundation.fmv
                    )}</td>
                </tr>
                <tr>
                    <td class="metric-label">EBITA Positive Impact</td>
                    <td>FMV × Corporate Tax Rate</td>
                    <td class="currency positive">${formatCurrency(
                      data.looptFoundation.ebitaPositiveImpact
                    )}</td>
                </tr>
                <tr>
                    <td class="metric-label">Net EBITA Contribution</td>
                    <td>Processing + Transportation + EBITA Impact</td>
                    <td class="currency ${
                      data.looptFoundation.netEbitaContribution >= 0
                        ? "positive"
                        : "negative"
                    }">${formatCurrency(
      data.looptFoundation.netEbitaContribution
    )}</td>
                </tr>
            </tbody>
        </table>
    </div>

    <div class="section">
        <div class="section-title">Revenue Opportunities</div>
        <table class="results-table">
            <thead>
                <tr>
                    <th>Revenue Channel</th>
                    <th>Quantity</th>
                    <th>Rate</th>
                    <th>Total Revenue</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class="metric-label">Rag Trader</td>
                    <td>${formatNumber(data.revenue.ragTraderWeight)} LBS</td>
                    <td class="currency">${formatCurrency(config.rag_trader_rate)}</td>
                    <td class="currency positive">${formatCurrency(
                      data.revenue.ragTraderRevenue
                    )}</td>
                </tr>
                <tr>
                    <td class="metric-label">Value Channel</td>
                    <td>${formatNumber(data.inputs.units)} Units</td>
                    <td class="currency">${formatCurrency(config.value_channel_rate)}</td>
                    <td class="currency positive">${formatCurrency(
                      data.revenue.valueChannelRevenue
                    )}</td>
                </tr>
                <tr style="background: #f0fdf4; font-weight: bold;">
                    <td class="metric-label">Total Revenue Potential</td>
                    <td colspan="2">Combined Channels</td>
                    <td class="currency positive">${formatCurrency(
                      data.revenue.ragTraderRevenue +
                        data.revenue.valueChannelRevenue
                    )}</td>
                </tr>
            </tbody>
        </table>
    </div>

    <div class="summary-cards">
        <div class="summary-card">
            <h4>Standard Process Total</h4>
            <div class="value" style="color: #dc2626;">-${formatCost(
              data.standardProcess.projectTotal
            )}</div>
        </div>
        <div class="summary-card">
            <h4>Donation Model Net Contribution</h4>
            <div class="value" style="color: #374151;">${formatCurrency(
              data.looptFoundation.netEbitaContribution
            )}</div>
        </div>
        <div class="summary-card">
            <h4>Total Revenue Potential</h4>
            <div class="value" style="color: #374151;">${formatCurrency(
              data.revenue.ragTraderRevenue + data.revenue.valueChannelRevenue
            )}</div>
            <div style="font-size: 10px; color: #6b7280; margin-top: 4px;">
                ${formatCurrency(
                  data.revenue.ragTraderRevenue +
                    data.revenue.valueChannelRevenue
                )} total of which rag is ${formatCurrency(
      data.revenue.ragTraderRevenue
    )} and value is ${formatCurrency(data.revenue.valueChannelRevenue)}
            </div>
        </div>
    </div>

    <div class="footer">
        <p>This report was generated by TextileCalc Pro - Professional Textile Cost Analysis Tool</p>
        <p>Report generated on ${reportDate} | All calculations based on current input parameters</p>
    </div>
</body>
</html>`;
  };

  const exportOptions = [
    {
      id: "pdf",
      label: "PDF Report",
      description: "Complete calculation report with company branding",
      icon: "FileText",
      format: "PDF",
    },
    {
      id: "csv",
      label: "CSV Data",
      description: "Raw data for spreadsheet analysis",
      icon: "Table",
      format: "CSV",
    },
  ];

  const handleExport = async (format) => {
    if (!calculationResults) {
      alert("No calculation data available to export");
      return;
    }

    setIsExporting(true);
    setExportFormat(format);

    try {
      if (format.toLowerCase() === "pdf") {
        // Generate PDF report
        const htmlContent = generatePDFReport(calculationResults);

        // Create a new window with the PDF content
        const printWindow = window.open("", "_blank");
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        // Wait for content to load, then trigger print dialog
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 500);
      } else if (format.toLowerCase() === "csv") {
        // Generate CSV data
        const csvContent = generateCSVReport(calculationResults);
        downloadFile(
          csvContent,
          `textile-calculation-${Date.now()}.csv`,
          "text/csv"
        );
      }
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Generate CSV report
  const generateCSVReport = (data) => {
    const rows = [
      ["Textile Cost Calculation Report"],
      ["Generated on", new Date().toLocaleDateString()],
      [""],
      ["INPUT SUMMARY"],
      ["Material Type", data.inputs.material],
      ["Standard Units", data.inputs.units],
      ["Standard Weight (LBS)", data.inputs.materialWeight],
      ["Donation Units", data.inputs.looptUnits],
      ["Donation Weight (LBS)", data.inputs.looptMaterialWeight],
      ["COGS per Unit", data.inputs.looptCogsPerUnit],
      ["Corporate Tax Rate (%)", data.inputs.looptCorporateTaxRate],
      [""],
      ["PROCESSING COSTS"],
      ["Method", "Processing Fee", "Transportation", "Total Cost"],
      [
        "Standard Process",
        Math.abs(data.standardProcess.processingFee),
        Math.abs(data.standardProcess.transportationEstimate),
        Math.abs(data.standardProcess.projectTotal),
      ],
      [
        "Donation Model",
        Math.abs(data.looptFoundation.processingFee),
        Math.abs(data.looptFoundation.transportationEstimate),
        Math.abs(
          data.looptFoundation.processingFee +
            data.looptFoundation.transportationEstimate
        ),
      ],
      [""],
      ["DONATION MODEL DETAILS"],
      ["Calculation", "Result"],
      ["COGS Gross", data.looptFoundation.cogsGross],
      ["Fair Market Value", data.looptFoundation.fmv],
      ["EBITA Positive Impact", data.looptFoundation.ebitaPositiveImpact],
      ["Net EBITA Contribution", data.looptFoundation.netEbitaContribution],
      [""],
      ["REVENUE OPPORTUNITIES"],
      ["Channel", "Weight (LBS)", "Rate per Pound", "Total Revenue"],
      [
        "Rag Trader",
        data.revenue.ragTraderWeight,
        config.rag_trader_rate,
        data.revenue.ragTraderRevenue,
      ],
      [
        "Value Channel",
        data.inputs.units,
        config.value_channel_rate,
        data.revenue.valueChannelRevenue,
      ],
      [
        "Total Revenue Potential",
        "",
        "",
        data.revenue.ragTraderRevenue + data.revenue.valueChannelRevenue,
      ],
    ];

    return rows
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
  };

  // Download file helper
  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEmailShare = async () => {
    if (!emailRecipient.trim()) {
      alert("Please enter a valid email address");
      return;
    }

    if (!calculationResults) {
      alert("No calculation data available to share");
      return;
    }

    setIsEmailSending(true);

    try {
      // Simulate email sending process (fake for now)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // TODO: Replace with actual email service integration
      console.log("Email would be sent to:", emailRecipient);
      console.log("Email content:", {
        subject: "Textile Cost Calculation Results",
        recipient: emailRecipient,
        material: calculationResults.inputs.material,
        standardCost: formatCost(
          calculationResults.standardProcess.projectTotal
        ),
        looptContribution: formatCurrency(
          calculationResults.looptFoundation.netEbitaContribution
        ),
        totalRevenue: formatCurrency(
          calculationResults.revenue.ragTraderRevenue +
            calculationResults.revenue.valueChannelRevenue
        ),
      });

      setEmailSuccess(true);
      // Auto-close popup after 3 seconds
      setTimeout(() => {
        setIsEmailPopupOpen(false);
        setEmailRecipient("");
        setEmailSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Email sending failed:", error);
      alert("Failed to send email. Please try again.");
    } finally {
      setIsEmailSending(false);
    }
  };

  const handleStartNewCalculation = () => {
    if (
      window.confirm(
        "Are you sure you want to start a new calculation? Current results will be cleared."
      )
    ) {
      navigate("/welcome-calculator-introduction");
    }
  };

  return (
    <div className="min-h-screen">
      <WizardProgressIndicator />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
              <Icon name="Download" size={24} color="white" />
            </div>
            <div>
              <h1 className="text-3xl font-heading-bold text-text-primary">
                Export
              </h1>
            </div>
          </div>
        </div>

        {calculationResults ? (
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
            {/* Export Content */}
            <div className="space-y-6">
              {/* Export Options */}
              <div className="bg-surface rounded-lg border border-border shadow-elevation-1 p-6">
                <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
                  Export Options
                </h3>

                <div className="space-y-3">
                  {exportOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleExport(option.format)}
                      disabled={isExporting}
                      className="w-full p-4 text-left bg-surface-50 hover:bg-primary-50 border border-border-light hover:border-primary rounded-lg transition-all duration-150 hover-scale focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                          <Icon
                            name={option.icon}
                            size={20}
                            className="text-accent"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="font-body-medium text-text-primary">
                            {option.label}
                          </div>
                          <div className="text-xs text-text-tertiary">
                            {option.description}
                          </div>
                        </div>
                        {isExporting &&
                          exportFormat === option.format.toLowerCase() && (
                            <div className="animate-spin">
                              <Icon
                                name="Loader2"
                                size={16}
                                className="text-accent"
                              />
                            </div>
                          )}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-border space-y-3">
                  <button
                    onClick={() => handleExport("PDF")}
                    disabled={isExporting}
                    className="w-full bg-primary text-white px-4 py-3 rounded-lg font-body-medium hover:bg-primary-600 transition-all duration-150 hover-scale focus-ring shadow-elevation-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      {isExporting && exportFormat === "pdf" ? (
                        <div className="animate-spin">
                          <Icon name="Loader2" size={16} color="white" />
                        </div>
                      ) : (
                        <Icon name="FileDown" size={16} color="white" />
                      )}
                      <span>
                        {isExporting && exportFormat === "pdf"
                          ? "Exporting..."
                          : "Export PDF Report"}
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="w-full bg-surface text-text-primary px-4 py-3 rounded-lg font-body-medium hover:bg-surface-100 border border-border hover:border-primary transition-all duration-150 hover-scale focus-ring"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="Eye" size={16} />
                      <span>
                        {showPreview ? "Hide Preview" : "Preview PDF Report"}
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* PDF Preview */}
              {showPreview && (
                <div className="bg-surface rounded-lg border border-border shadow-elevation-1 p-6">
                  <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
                    PDF Report Preview
                  </h3>

                  <div className="bg-white border border-border-light rounded-lg p-6 max-h-96 overflow-y-auto">
                    <div className="text-center mb-6">
                      <h1 className="text-2xl font-bold text-primary mb-2">
                        Textile Cost Calculation Report
                      </h1>
                      <div className="text-sm text-gray-600 mb-1">
                        Comprehensive Analysis of Processing Costs and Revenue
                        Opportunities
                      </div>
                      <div className="text-xs text-gray-500">
                        Generated on{" "}
                        {new Date().toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </div>
                    </div>

                    <div className="space-y-6">
                      {/* Input Summary Preview */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 mb-3 bg-gray-100 px-3 py-2 rounded border-l-4 border-primary">
                          Input Summary
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div className="bg-gray-50 p-3 rounded">
                            <div className="font-semibold text-gray-700 mb-2">
                              Material Information
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Material Type:
                                </span>
                                <span className="font-medium">
                                  {calculationResults.inputs.material}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Standard Units:
                                </span>
                                <span className="font-medium">
                                  {formatNumber(
                                    calculationResults.inputs.units
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Standard Weight:
                                </span>
                                <span className="font-medium">
                                  {formatNumber(
                                    calculationResults.inputs.materialWeight
                                  )}{" "}
                                  LBS
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <div className="font-semibold text-gray-700 mb-2">
                              Donation Model Inputs
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Donation Units:
                                </span>
                                <span className="font-medium">
                                  {formatNumber(
                                    calculationResults.inputs.looptUnits
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  COGS per Unit:
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(
                                    calculationResults.inputs.looptCogsPerUnit
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Tax Rate:</span>
                                <span className="font-medium">
                                  {formatNumber(
                                    calculationResults.inputs
                                      .looptCorporateTaxRate
                                  )}
                                  %
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Processing Costs Preview */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 mb-3 bg-gray-100 px-3 py-2 rounded border-l-4 border-primary">
                          Processing Cost Analysis
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-primary text-white">
                                <th className="border border-primary px-2 py-2 text-left">
                                  Processing Method
                                </th>
                                <th className="border border-primary px-2 py-2 text-right">
                                  Processing Fee
                                </th>
                                <th className="border border-primary px-2 py-2 text-right">
                                  Transportation
                                </th>
                                <th className="border border-primary px-2 py-2 text-right">
                                  Total Cost
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="bg-gray-50">
                                <td className="border border-gray-300 px-2 py-2 font-medium">
                                  Standard Process
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-right text-red-600 font-semibold">
                                  {formatCost(
                                    calculationResults.standardProcess
                                      .processingFee
                                  )}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-right text-red-600 font-semibold">
                                  {formatCost(
                                    calculationResults.standardProcess
                                      .transportationEstimate
                                  )}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-right text-red-600 font-semibold">
                                  {formatCost(
                                    calculationResults.standardProcess
                                      .projectTotal
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-2 font-medium">
                                  Donation Model
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-right text-red-600 font-semibold">
                                  {formatCost(
                                    calculationResults.looptFoundation
                                      .processingFee
                                  )}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-right text-red-600 font-semibold">
                                  {formatCost(
                                    calculationResults.looptFoundation
                                      .transportationEstimate
                                  )}
                                </td>
                                <td className="border border-gray-300 px-2 py-2 text-right text-red-600 font-semibold">
                                  {formatCost(
                                    calculationResults.looptFoundation
                                      .processingFee +
                                      calculationResults.looptFoundation
                                        .transportationEstimate
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Revenue Summary Preview */}
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 mb-3 bg-gray-100 px-3 py-2 rounded border-l-4 border-primary">
                          Revenue Opportunities
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-red-50 p-3 rounded border border-red-200 text-center">
                            <div className="text-xs text-red-600 font-semibold mb-1">
                              Standard Process Total
                            </div>
                            <div className="text-sm font-bold text-red-800">
                              -
                              {formatCost(
                                calculationResults.standardProcess.projectTotal
                              )}
                            </div>
                          </div>
                          <div className="bg-success-50 p-3 rounded border border-success-200 text-center">
                            <div className="text-xs text-success-600 font-semibold mb-1">
                              Donation Model Net Contribution
                            </div>
                            <div className="text-sm font-bold text-text-primary">
                              {formatCurrency(
                                calculationResults.looptFoundation
                                  .netEbitaContribution
                              )}
                            </div>
                          </div>
                          <div className="bg-success-50 p-3 rounded border border-success-200 text-center">
                            <div className="text-xs text-success-600 font-semibold mb-1">
                              Total Revenue Potential
                            </div>
                            <div className="text-sm font-bold text-text-primary">
                              {formatCurrency(
                                calculationResults.revenue.ragTraderRevenue +
                                  calculationResults.revenue.valueChannelRevenue
                              )}
                            </div>
                            <div className="text-xs text-text-tertiary mt-1">
                              {formatCurrency(
                                calculationResults.revenue.ragTraderRevenue +
                                  calculationResults.revenue.valueChannelRevenue
                              )}{" "}
                              total of which rag is{" "}
                              {formatCurrency(
                                calculationResults.revenue.ragTraderRevenue
                              )}{" "}
                              and value is{" "}
                              {formatCurrency(
                                calculationResults.revenue.valueChannelRevenue
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                      <p className="text-xs text-gray-500">
                        This report was generated by TextileCalc Pro -
                        Professional Textile Cost Analysis Tool
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Sharing */}
              <div className="bg-surface rounded-lg border border-border shadow-elevation-1 p-6">
                <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
                  Share Results
                </h3>

                <button
                  onClick={() => setIsEmailPopupOpen(true)}
                  className="w-full p-4 text-left bg-primary-50 hover:bg-primary-100 border border-primary-200 hover:border-primary rounded-lg transition-all duration-150 hover-scale focus-ring"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Icon name="Mail" size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-body-medium text-primary">
                        Email Results
                      </div>
                      <div className="text-xs text-text-tertiary">
                        Share calculation results via email
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleStartNewCalculation}
                  className="w-full bg-primary text-white px-4 py-3 rounded-lg font-body-medium hover:bg-primary-600 hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus-ring shadow-elevation-1"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Plus" size={16} color="white" />
                    <span>Start New Calculation</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* No Data State */
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-warning" />
            </div>
            <h3 className="text-xl font-heading-semibold text-text-primary mb-2">
              No Calculation Data Found
            </h3>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">
              There's no calculation data available to export. Please complete a
              calculation first.
            </p>
            <button
              onClick={() => navigate("/welcome-calculator-introduction")}
              className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg font-body-medium hover:bg-primary-600 hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus-ring shadow-elevation-1"
            >
              <Icon name="ArrowLeft" size={20} color="white" className="mr-2" />
              Start New Calculation
            </button>
          </div>
        )}
      </main>

      {/* Email Popup Modal */}
      {isEmailPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-elevation-3 p-6 w-full max-w-md mx-4">
            {emailSuccess ? (
              /* Success State */
              <div className="text-center">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} className="text-success" />
                </div>
                <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
                  Email Sent Successfully!
                </h3>
                <p className="text-text-secondary mb-4">
                  Your calculation results have been sent to{" "}
                  <span className="font-medium text-text-primary">
                    {emailRecipient}
                  </span>
                </p>
                <div className="bg-success-50 p-3 rounded-lg border border-success-200 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Info" size={16} className="text-success" />
                    <span className="text-sm text-success-700">
                      This popup will close automatically in a few seconds
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsEmailPopupOpen(false);
                    setEmailRecipient("");
                    setEmailSuccess(false);
                  }}
                  className="w-full px-4 py-3 bg-success text-white rounded-lg text-sm font-body-medium hover:bg-success-600 transition-all duration-150 focus-ring"
                >
                  Close
                </button>
              </div>
            ) : (
              /* Email Form State */
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading-semibold text-text-primary">
                    Share Results via Email
                  </h3>
                  <button
                    onClick={() => {
                      setIsEmailPopupOpen(false);
                      setEmailRecipient("");
                      setEmailSuccess(false);
                    }}
                    className="text-text-tertiary hover:text-text-primary transition-colors duration-150"
                    disabled={isEmailSending}
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="popup-email-recipient"
                      className="block text-sm font-body-medium text-text-primary mb-2"
                    >
                      Recipient Email Address
                    </label>
                    <input
                      type="email"
                      id="popup-email-recipient"
                      value={emailRecipient}
                      onChange={(e) => setEmailRecipient(e.target.value)}
                      placeholder="colleague@company.com"
                      className="w-full px-3 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                      disabled={isEmailSending}
                    />
                  </div>

                  <div className="bg-surface-50 p-3 rounded-lg border border-border-light">
                    <div className="text-xs text-text-secondary mb-2">
                      Email will include:
                    </div>
                    <ul className="text-xs text-text-tertiary space-y-1">
                      <li>• Material information and inputs</li>
                      <li>• Processing cost comparison</li>
                      <li>• Revenue opportunity summary</li>
                      <li>• Key financial metrics</li>
                    </ul>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setIsEmailPopupOpen(false);
                        setEmailRecipient("");
                        setEmailSuccess(false);
                      }}
                      disabled={isEmailSending}
                      className="flex-1 px-4 py-3 bg-surface text-text-secondary border border-border rounded-lg text-sm font-body-medium hover:bg-surface-100 transition-all duration-150 focus-ring disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEmailShare}
                      disabled={isEmailSending || !emailRecipient.trim()}
                      className="flex-1 px-4 py-3 bg-primary text-white rounded-lg text-sm font-body-medium hover:bg-primary-600 transition-all duration-150 focus-ring shadow-elevation-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {isEmailSending ? (
                          <div className="animate-spin">
                            <Icon name="Loader2" size={16} color="white" />
                          </div>
                        ) : (
                          <Icon name="Send" size={16} color="white" />
                        )}
                        <span>
                          {isEmailSending ? "Sending..." : "Send Email"}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportSummary;
